import { create } from "zustand";
import { fetchUserCreditBalance, fetchActiveRequestCost } from "apis/creditService";

// Shared credit balance so any part of the app (e.g. the request-update flow)
// can optimistically deduct credits and the Sidebar reflects it immediately,
// then reconcile with the server in the background.
interface CreditState {
  availableCredits: number | null;
  // Tokens deducted per posted load request, from the active credit config
  // (Admin > Token Management). Null until first fetched.
  requestCost: number | null;
  loading: boolean;
  refresh: () => Promise<void>;
  refreshRequestCost: () => Promise<void>;
  decrement: (by?: number) => void;
}

// De-dupes concurrent refreshes: multiple components (Sidebar, CreditsButton,
// the request-update flow) and React StrictMode's double-mount all share one
// in-flight network request instead of firing several identical calls.
let inFlight: Promise<void> | null = null;
let requestCostInFlight: Promise<void> | null = null;

export const useCreditStore = create<CreditState>((set) => ({
  availableCredits: null,
  requestCost: null,
  loading: false,
  refresh: () => {
    if (inFlight) return inFlight;
    set({ loading: true });
    inFlight = (async () => {
      try {
        const res = await fetchUserCreditBalance();
        set({ availableCredits: Number(res.availableCredits), loading: false });
      } catch (err) {
        console.error("Failed to fetch credit balance:", err);
        set({ loading: false });
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  },
  refreshRequestCost: () => {
    if (requestCostInFlight) return requestCostInFlight;
    requestCostInFlight = (async () => {
      try {
        const cost = await fetchActiveRequestCost();
        set({ requestCost: cost });
      } catch (err) {
        console.error("Failed to fetch active request cost:", err);
      } finally {
        requestCostInFlight = null;
      }
    })();
    return requestCostInFlight;
  },
  decrement: (by = 1) =>
    set((state) => ({
      availableCredits:
        state.availableCredits == null
          ? state.availableCredits
          : Math.max(0, state.availableCredits - by),
    })),
}));
